import type { Question, FormEngineTheme, QuestionType } from "@squaredr/fieldcraft-core";

/** Props passed to every field component. */
export type FieldProps = {
  field: Question;
  value: unknown;
  error?: string[];
  touched: boolean;
  disabled: boolean;
  readonly: boolean;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  onFocus: () => void;
  theme: FormEngineTheme;
  customProps?: Record<string, unknown>;
  /** All current field values in the form. Available for fields that need cross-field access (e.g., AppointmentField reading a timezone selector). @since 1.8.0 */
  fieldValues?: Record<string, unknown>;
};

/** A React component that renders a single field. */
export type FieldComponent = React.ComponentType<FieldProps>;

/** Maps QuestionType → FieldComponent. Partial because not all types need to be registered. */
export type FieldRegistry = Partial<Record<QuestionType, FieldComponent>>;

/** Create a mutable field registry. */
export function createFieldRegistry(
  initial?: FieldRegistry,
): FieldRegistry {
  return { ...initial };
}

/** Merge multiple registries. Later registries override earlier ones. */
export function mergeRegistries(
  ...registries: (FieldRegistry | undefined)[]
): FieldRegistry {
  const merged: FieldRegistry = {};
  for (const reg of registries) {
    if (reg) Object.assign(merged, reg);
  }
  return merged;
}
